/**
 * InventoryLayout — Issue #228
 *
 * 库存模块顶部子导航。和 SchedulingLayout / LabLayout 保持一致的视觉风格。
 *
 * 权限闸门：
 * - 顶部子导航按当前会话的库存权限可见性过滤。
 * - **路由级**：当用户直接访问当前激活的 `/inventory/*` 路由但缺少对应读权限
 *   （例如 `inventory.consumable:read` / `inventory.movement:read`）时，
 *   渲染受控的 `AccessDenied`（403）状态，而不是继续渲染业务内容。
 *
 * 这是因为当前后端 `/inventory/*` 控制器主要依赖租户作用域而非 `@RequirePermission`
 * （参见 `inventoryPermissions.ts` 的说明），所以前端必须显式拦截无权限的直访。
 */
import { type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import {
  canSeeInventoryConsumables,
  canSeeInventoryMovements,
} from '@/lib/inventoryPermissions';

export interface InventoryNavItem {
  readonly href: string;
  readonly label: string;
  readonly visible: (permissions: readonly string[]) => boolean;
}

export const INVENTORY_NAV_ITEMS: readonly InventoryNavItem[] = [
  {
    href: '/inventory/consumables',
    label: '耗材库存',
    visible: canSeeInventoryConsumables,
  },
  {
    href: '/inventory/movements',
    label: '库存流水',
    visible: canSeeInventoryMovements,
  },
];

/**
 * Resolve the inventory nav item that matches the current pathname, if any.
 * The match uses `pathname.startsWith(item.href)` so detail/child routes under
 * a section (e.g. a future `/inventory/consumables/:id`) still count as that
 * section for permission gating.
 */
export function selectActiveInventoryNavItem(
  items: readonly InventoryNavItem[],
  pathname: string,
): InventoryNavItem | undefined {
  return items.find((item) => pathname.startsWith(item.href));
}

export function selectVisibleInventoryNavItems(
  items: readonly InventoryNavItem[],
  permissions: readonly string[],
): readonly InventoryNavItem[] {
  return items.filter((item) => item.visible(permissions));
}

export default function InventoryLayout({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const { permissions } = useAuth();
  const visibleItems = selectVisibleInventoryNavItems(INVENTORY_NAV_ITEMS, permissions);
  const activeItem = selectActiveInventoryNavItem(INVENTORY_NAV_ITEMS, location);
  const activeDenied = activeItem !== undefined && !activeItem.visible(permissions);

  return (
    <div className="flex flex-col gap-6">
      <div className="border-b border-border">
        <nav className="flex gap-8 px-6 py-3" aria-label="库存子导航">
          {visibleItems.map((item) => {
            const active = location.startsWith(item.href);
            return (
              <a
                key={item.href}
                href={item.href}
                className={cn(
                  'text-sm font-medium transition-colors',
                  active
                    ? 'border-b-2 border-primary text-primary pb-3'
                    : 'text-muted-foreground hover:text-foreground pb-3',
                )}
              >
                {item.label}
              </a>
            );
          })}
        </nav>
      </div>
      <div className="px-6">
        {activeDenied ? <AccessDenied resource={activeItem.label} /> : children}
      </div>
    </div>
  );
}
